import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationItem from './NotificationItem';
import { markAllNotificationsAsRead } from '../utils/notificationApi';
import {
  getNotificationCache,
  refreshNotifications,
} from '../hooks/useNotificationPreloader';
import BellIcon from '../assets/bell.svg';
import style from '../assets/style/NotificationDropdown.module.css';

const NotificationDropdown = ({ user }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);

  useEffect(() => {
    if (user) {
      // Load cached notifications immediately
      loadCachedNotifications();

      // Set up polling to sync with cache updates
      const interval = setInterval(loadCachedNotifications, 1000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isOpen && user) {
      // Trigger background refresh and load latest cache
      refreshNotifications();
      loadCachedNotifications();
    }
  }, [isOpen, user]);

  const loadCachedNotifications = () => {
    try {
      const cache = getNotificationCache();
      setNotifications(cache.notifications || []);
      setUnreadCount(cache.unreadCount || 0);
      setError(null);
    } catch (error) {
      console.error('Error loading cached notifications:', error);
      setError('Failed to load notifications');
    }
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = (notificationId) => {
    // Update local state immediately for better UX
    setNotifications((prevNotifications) =>
      prevNotifications.map((notification) =>
        notification.notificationID === notificationId
          ? { ...notification, isRead: true }
          : notification
      )
    );

    // Update unread count
    setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllAsRead(true);
      await markAllNotificationsAsRead();

      // Update local state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
      setError('Failed to mark all notifications as read');
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const handleViewAllNotifications = () => {
    navigate('/notifications');
    setIsOpen(false);
  };

  const handleNotificationClick = () => {
    setIsOpen(false); // Close dropdown when notification is clicked
  };

  if (!user) {
    return null;
  }

  return (
    <div className={style.notificationDropdown} ref={dropdownRef}>
      <button
        className={style.notificationButton}
        onClick={handleToggleDropdown}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <img src={BellIcon} alt="Notifications" className={style.bellIcon} />
        {unreadCount > 0 && (
          <span className={style.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={style.dropdownPanel}>
          <div className={style.dropdownHeader}>
            <h3 className={style.dropdownTitle}>Notifications</h3>
            <div className={style.headerActions}>
              {unreadCount > 0 && (
                <button
                  className={style.markAllButton}
                  onClick={handleMarkAllAsRead}
                  disabled={markingAllAsRead}
                >
                  {markingAllAsRead ? 'Marking...' : 'Mark all read'}
                </button>
              )}
            </div>
          </div>

          {unreadCount > 0 && (
            <div className={style.unreadBanner}>
              <span className={style.unreadText}>
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          <div className={style.dropdownContent}>
            {error ? (
              <div className={style.errorContainer}>
                <div className={style.errorMessage}>{error}</div>
                <button
                  className={style.retryButton}
                  onClick={() => {
                    refreshNotifications();
                    loadCachedNotifications();
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className={style.emptyContainer}>
                <div className={style.emptyIcon}>🔔</div>
                <h4 className={style.emptyTitle}>No notifications</h4>
                <p className={style.emptyMessage}>You're all caught up!</p>
              </div>
            ) : (
              <div className={style.notificationsList}>
                {notifications.slice(0, 8).map((notification) => (
                  <div
                    key={notification.notificationID}
                    onClick={handleNotificationClick}
                  >
                    <NotificationItem
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                      compact={true}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={style.dropdownFooter}>
            <button
              className={style.viewAllButton}
              onClick={handleViewAllNotifications}
            >
              {notifications.length > 8
                ? `View all ${notifications.length} notifications`
                : 'View notification history'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
